<script setup lang="ts">
import {reactive, nextTick, ref, watch, computed} from 'vue';
import TreeSelect from 'primevue/treeselect';
import Select from 'primevue/select';
import {TreeNode} from "primevue/treenode";
import LoginTest from "../../components/LoginTest.vue";
import {useGlobalStore} from "../../stores/global.ts";
import Checkbox from "primevue/checkbox";
import Address from "../../components/Address.vue";
import { getReCaptchaToken } from '../../js/get_recaptcha_token';

const props = defineProps({
  url: {
    type: String,
    required: true
  },
  verify_email_url: {
    type: String,
    required: true
  },
  login_url: {
    type: String,
    required: true
  },
  forgot_password_url: {
    type: String,
    default: ''
  },
  searchRequest: {
    type: Object,
    required: true,
  },
  sectors: {
    type: Array<TreeNode>,
    required: true
  },
  regions: {
    type: Array<TreeNode>,
    required: true
  },
  purposes: {
    type: Array<TreeNode>,
    required: true
  },
  turnovers : {
    type: Array<TreeNode>,
    required: true
  },
  typeOfAcquisition: {
    type: Array<TreeNode>,
    required: true
  },
  typeOfTransaction: {
    type: Array<TreeNode>,
    required: true
  },
  lifePhaseEnterprise: {
    type: Array<TreeNode>,
    required: true
  },
  legalEntity: {
    type: Array<TreeNode>,
    required: true
  },
  earningBeforeTaxes: {
    type: Array<TreeNode>,
    required: true
  },
  employeesInFTE: {
    type: Array<TreeNode>,
    required: true
  },
  askingPrice: {
    type: Array<TreeNode>,
    required: true
  },
  contract: {
    type: Array<TreeNode>,
    required: true
  },
  formOfAddress: {
    type: Array<TreeNode>,
    required: true
  },
  trans: {type: Object, required: true}
});

let saving = ref(false);
let error_msg = ref('');
let success_msg = ref('');
let agreeTermsValue = ref(false);

const formRef = ref<HTMLElement | null>(null);
const errorRef = ref<HTMLElement | null>(null);
const store = useGlobalStore();

let mySearchRequest = reactive(props.searchRequest);

const location_search_params = new URLSearchParams(window.location.search);
const purpose = location_search_params.get('purpose');
if (purpose) {
  mySearchRequest.idPurpose = parseInt(purpose);
}
const is_sale = computed(() => mySearchRequest.idPurpose == 2);
watch(is_sale, () => {
  mySearchRequest.typeOfCompany = [];
});

let computedSectors = computed(() => {
  if (!is_sale) {
    return props.sectors;
  }

  return props.sectors.map((sector) => {
    if (sector.children && sector.children.length > 0) {
      return {
        ...sector,
        selectable: false,
        children: sector.children.map((child) => ({
          ...child,
          selectable: true,
        })),
      };
    }
    return {
      ...sector,
      selectable: true,
    };
  });
});

let expandedRegions: { [key: string]: boolean } = {};
props.regions.forEach(e => {
  expandedRegions[e.key] = true;
  e.children?.forEach(ee => {
    expandedRegions[ee.key] = true;
  });
});

let expandedSectors: { [key: string]: boolean } = {};
props.sectors.forEach(e => {
  expandedSectors[e.key] = true;
  e.children?.forEach(ee => {
    expandedSectors[ee.key] = true;
  });
});
/*
onMounted(() => {
  console.log(mySearchRequest);

  console.log('props.sectors', props.sectors);
  console.log('props.regions', props.regions);
  console.log('props.purposes', props.purposes);
  console.log('props.turnovers', props.turnovers);
  console.log('props.typeOfAcquisition', props.typeOfAcquisition);
  console.log('props.typeOfTransaction', props.typeOfTransaction);
  console.log('props.lifePhaseEnterprise', props.lifePhaseEnterprise);
  console.log('props.legalEntity', props.legalEntity);
  console.log('props.earningBeforeTaxes', props.earningBeforeTaxes);
  console.log('props.employeesInFTE', props.employeesInFTE);
  console.log('props.askingPrice', props.askingPrice);
  console.log('contract', props.contract);
});
*/

let login_test_step = ref('verify_email');
let form_readonly = false;

if (store.user.id) {
  login_test_step.value = 'reaction';
  form_readonly = true;
}

let new_password = ref('');
let verify_password = ref('');
let saved = ref(false);

watch(login_test_step, async (_new) => {
  switch (_new) {
    case 'success':
    case 'register':
      login_test_step.value = 'reaction';
      // Refresh page
      if (store.user.id) {
        window.location.reload();
      }
      break;
    case 'reaction':
      await nextTick(() => {
        formRef.value?.querySelector('input')?.focus();
      });
      break;
  }
});

let showContractField = (store.webspace != 'btkbe' && store.webspace != 'btkes')  && (
    (mySearchRequest.id && mySearchRequest.hasExpired) || !mySearchRequest.id
);

let validation_run = ref(false);

function isInvalidSelect(key: string, vr: boolean = true) {

  // Skip contract field if not shown
  if (key == 'contract' && !showContractField) {
    return false;
  }

  if (vr && !validation_run.value) {
    return false;
  }
  let v = mySearchRequest[key];

  switch (typeof v) {
    case 'object':
      return (Object.keys(v).length < 1);
    case 'string':
      return (v.length < 1);
  }

  return !(v > 0);
}

const validateForm = (): boolean => {
  let valid = true;
  let isNewUser = !store.user.id;
  let u = store.user;
  let a = u.address;

  if (isNewUser) {
    valid = !(
        (!u.firstName || !u.lastName) ||
        (!a.postcode || !a.number || !a.street || !a.town) ||
        (!u.email || !u.phone || !u.phoneMobile)
    );
  }

  valid = valid && !(
      !agreeTermsValue.value ||
      isInvalidSelect('typeOfCompany', false) ||
      isInvalidSelect('region', false) ||
      isInvalidSelect('employeesInFTE', false) ||
      isInvalidSelect('turnoverLastFinancialYear', false) ||
      isInvalidSelect('typeOfTransaction', false) ||
      isInvalidSelect('lifePhaseEnterprise', false) ||
      isInvalidSelect('legalEntity', false) ||
      isInvalidSelect('earningBeforeTaxes', false) ||
      isInvalidSelect('askingPrice', false) ||
      isInvalidSelect('idPurpose', false) ||
      isInvalidSelect('contract', false)
  );

  return valid;
}

const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const submitForm = async () => {
  error_msg.value = '';
  success_msg.value = '';
  saved.value = false;

  if (!validateForm()) {
    validation_run.value = true;

    await nextTick(() => {
      let el = formRef.value?.querySelector('.p-invalid') as HTMLElement;
      if (el) {
        el.scrollIntoView({ behavior: isReducedMotion ? 'auto' : 'smooth' });
      }
    })
    return;
  } else {
    validation_run.value = false;
  }
  saving.value = true;

  try {
    const token = await getReCaptchaToken('save_search_service');

    const res = await fetch(props.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        'g-recaptcha-response': token,
        'user': store.user,
        'search_service': mySearchRequest,
        'agree_terms': agreeTermsValue.value,
        'password': new_password.value,
        'passwordRepeat': verify_password.value,
      }),
    });

    const data = await res.json();

    if (data.success) {

      if (data.code == 3) {

        if (!data.order_id) {
          throw new Error('Order ID is empty');
        }

        try {
          const paymentResponse = await fetch(`/api/payment/paymentlink/searchrequest?order_id=${data.order_id}&locale=${store.locale}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!paymentResponse.ok) {
            throw new Error('Failed to fetch payment link');
          }

          const model = await paymentResponse.json();
          window.location.href = model.paymentlink;

        } catch (e) {
          console.error(e);
          error_msg.value = 'Failed to fetch payment link';
        }
      } else if (data.code == 2) {
        // Skip payment and show success message
        success_msg.value = data.message;
        saved.value = true;
      }
    } else {
      error_msg.value = data.message;
      errorRef.value?.scrollIntoView({behavior: isReducedMotion ? 'auto' : 'smooth'});
    }
  } catch (e) {
    console.error(e);
    error_msg.value = 'Failed to save data';
  }

  saving.value = false;
};

</script>

<template>
  <p v-if="saved"><strong>{{ trans.success_msg }}</strong></p>
  <form v-if="!saved" ref="formRef" id="form-signup" :class="['form-default', 'form-signup form-search-service form--no-pad form']"
        action="" method="post">
    <div>
      <LoginTest v-if="login_test_step === 'verify_email' || login_test_step === 'login'"
                 v-model:step="login_test_step" :verify_email_url="verify_email_url"
                 :login_url="login_url" :forgot_password_url="forgot_password_url" :trans="trans"></LoginTest>

      <!-- Step 2 -->
      <div v-if="login_test_step === 'reaction'">
        <div class="form-row" v-if="!purpose">
          <label for="purpose">{{ trans.type }}</label>
          <div id="purpose" class="inputs-group">
            <label class="ui-radio" :class="{'p-invalid': isInvalidSelect('idPurpose')}">
              <input name="purpose" type="radio" value="1" v-model="mySearchRequest.idPurpose">
              <span>{{ trans.type_offered }}</span>
            </label>
            <label class="ui-radio":class="{'p-invalid': isInvalidSelect('idPurpose')}">
              <input name="purpose" type="radio" value="2" v-model="mySearchRequest.idPurpose">
              <span>{{ trans.type_requested }}</span>
            </label>
          </div>
        </div>
        <div class="form-row">
          <label>{{ trans.sector }}</label>
          <TreeSelect v-model="mySearchRequest.typeOfCompany"
                      :options="computedSectors"
                      :selectionMode="is_sale ? 'single' : 'checkbox'"
                      filter filterMode="lenient"
                      :expandedKeys="expandedSectors"
                      :class="{'p-invalid': isInvalidSelect('typeOfCompany')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <label>{{ trans.region }}</label>
          <TreeSelect v-model="mySearchRequest.region"
                      :options="regions"
                      :expandedKeys="expandedRegions"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('region')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <div id="locationAnonymous" class="inputs-group">
            <label class="ui-checkbox">
              <input type="checkbox" :value="1" v-model="mySearchRequest.locationAnonymous">
              <span>{{ trans.locationAnonymous }}</span>
            </label>
          </div>
        </div>
        <div class="form-row">
          <label>{{ trans.employees_fte }}</label>
          <TreeSelect v-model="mySearchRequest.employeesInFTE"
                      :options="employeesInFTE"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('employeesInFTE')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>

        <div class="form-row">
          <label>{{ trans.legal_form }}</label>
          <TreeSelect v-model="mySearchRequest.legalEntity"
                      :options="legalEntity"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('legalEntity')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <label>{{ trans.type_of_transaction }}</label>
          <TreeSelect v-model="mySearchRequest.typeOfTransaction"
                      :options="typeOfTransaction"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('typeOfTransaction')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <label>{{ trans.type_of_aquisition }}</label>
          <div id="purpose" class="inputs-group">
            <label v-for="option in typeOfAcquisition" :key="option.key" class="ui-checkbox" :class="{'p-invalid': isInvalidSelect('typeOfAcquisition')}">
              <input type="checkbox" :value="option.key" v-model="mySearchRequest.typeOfAcquisition">
              <span>{{ option.label }}</span>
            </label>
          </div>
        </div>
        <div class="form-row">
          <label>{{ trans.life_phase_of_company }}</label>
          <TreeSelect v-model="mySearchRequest.lifePhaseEnterprise"
                      :options="lifePhaseEnterprise"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('lifePhaseEnterprise')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <label>{{ trans.turnover }}</label>
          <TreeSelect v-model="mySearchRequest.turnoverLastFinancialYear"
                      :options="turnovers"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('turnoverLastFinancialYear')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <label>{{ trans.earnings }}</label>
          <TreeSelect v-model="mySearchRequest.earningBeforeTaxes"
                      :options="earningBeforeTaxes"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('earningBeforeTaxes')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>
        <div class="form-row">
          <label>{{ trans.price }}</label>
          <TreeSelect v-model="mySearchRequest.askingPrice"
                      :options="askingPrice"
                      selectionMode="checkbox"
                      :class="{'p-invalid': isInvalidSelect('askingPrice')}"
                      :placeholder="trans.select">
          </TreeSelect>
        </div>

        <template v-if="!store.user.id">
          <hr>

          <h3>{{ trans.your_info }}</h3>
          <!--      <div class="form-row">-->
          <!--        <label for="reaction-form-form-of-address">{{ trans.formOfAddress }}</label>-->
          <!--        <div class="inputs-group inline">-->
          <!--          <label v-for="fa in formOfAddress" class="ui-radio">-->
          <!--            <input v-model="store.user.formOfAddress" id="reaction-form-form-of-address" name="formOfAddress" type="radio" :value="fa.id">-->
          <!--            <span>{{ fa.label }}</span>-->
          <!--          </label>-->
          <!--        </div>-->
          <!--      </div>-->
          <div class="form-row">
            <div class="row">
              <div class="col-1-of-2">
                <label for="firstName">{{ trans.first_name }}</label>
                <input v-model="store.user.firstName" :class="{'p-invalid': validation_run && !store.user.firstName} "
                       id="firstName" type="text" placeholder="" required :readonly="form_readonly" />
              </div>
              <div class="col-1-of-2">
                <label for="lastName">{{ trans.last_name }}</label>
                <input v-model="store.user.lastName" :class="{'p-invalid': validation_run && !store.user.lastName}"
                       id="lastName" type="text" placeholder="" required :readonly="form_readonly" />
              </div>
            </div>
          </div>

          <Address :trans="trans.address" :validation_run v-model:postcode="store.user.address.postcode"
                   v-model:number="store.user.address.number"
                   v-model:street="store.user.address.street"
                   v-model:town="store.user.address.town" />

          <div class="form-row">
            <div class="row">
              <div class="col-1-of-2">
                <label for="phone">{{ trans.phone }}</label>
                <input v-model="store.user.phone" id="phone" type="text" name="phone" required :readonly="form_readonly">
              </div>
              <div class="col-1-of-2">
                <label for="mobile">{{ trans.mobile }}</label>
                <input v-model="store.user.phoneMobile" id="mobile" type="text" name="mobile" required :readonly="form_readonly">
              </div>
            </div>
          </div>

          <div class="flow">
            <h2>{{ trans.create_password }}</h2>
          </div>
          <div class="row">
            <div>
              <label for="reaction-form-new-password">{{ trans.password }}</label>
              <input v-model="new_password" :class="{'p-invalid': validation_run && !new_password}"
                     id="reaction-form-new-password" type="password" minlength="6"
                     autocomplete="new-password" placeholder="" required>
            </div>

            <div>
              <label for="reaction-form-verify-password">{{ trans.password_repeat }}</label>
              <input v-model="verify_password" :class="{'p-invalid': validation_run && !(verify_password && (new_password === verify_password))}"
                     id="reaction-form-verify-password" type="password" minlength="6"
                     autocomplete="new-password" placeholder="" required>
            </div>
          </div>
        </template>

        <hr v-if="showContractField">

        <div v-if="showContractField" class="form-row">
          <label>{{ trans.contract }}</label>
          <Select v-model="mySearchRequest.contract"
                  :options="contract"
                  optionLabel="label" optionValue="id"
                  :class="{'p-invalid': isInvalidSelect('contract')}"
                  :placeholder="trans.select">
          </Select>
        </div>

        <hr>

        <label class="checkbox-wrap">
          <Checkbox v-model="agreeTermsValue" :invalid="validation_run && !agreeTermsValue" binary/>
          <span v-html="trans.agree_terms"></span>
        </label>
        <div ref="errorEl" v-if="error_msg" class="notice notice-error" v-html="error_msg"></div>

        <div class="form-controls">
          <span></span>

          <button :disabled="saving" @click.prevent="submitForm" class="btn-solid icon-rarr"
                  :class="saving ? 'icon-spinner' : ''">
            {{ trans.confirm }}
          </button>
        </div>
      </div>
    </div>
  </form>

  <div v-if="success_msg" class="notice notice-success" v-html="success_msg"></div>
</template>
